import { Routes } from '@angular/router';
import validAccountDetails from './guards/valid-account-details.guard';
import isMembershipStatusAllowed from './guards/is-membership-status-allowed.guard';
import { AccountStatus } from '../../types';
import setMembershipRenewAccountDetails from './guards/set-membership-renew-accountdetails.guard';
import setMembershipAssociateAddAccountDetails from './guards/set-membership-add-accountdetails.guard';
import setMembershipUpdateAccountDetails from './guards/set-membership-update-accountdetails.guard';
import { redirectToRenewIfBalanceDue } from './guards/redirect-to-renew-if-balance-due.guard';
import waitForMembershipLoadGuard from './guards/waitForMembershipLoad.guard';
import canChangeMembership from './guards/can-change-membership.guard';
import { membershipAutoRenewRoutes } from '../membership-auto-renew/membership-auto-renew-routing';
import { Breadcrumb } from '../../constants/pages';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [],
    loadComponent: () => import('../account-status/account-status').then((c) => c.AccountStatusComponent),
  },
  {
    path: '',
    outlet: 'footer',
    pathMatch: 'full',
    loadComponent: () => import('../gift-card/gift-card').then((c) => c.GiftCardComponent),
  },
  {
    path: 'information',
    canActivate: [validAccountDetails],
    loadComponent: () =>
      import('../account-information/account-information').then((c) => c.AccountInformationComponent),
    data: {
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountInformation],
    },
  },
  {
    path: 'information/address',
    canActivate: [validAccountDetails],
    loadComponent: () => import('../account-address/account-address').then((m) => m.AccountAddressComponent),
    data: {
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountInformation, Breadcrumb.accountInformationAddress],
    },
  },
  {
    path: 'information/phone',
    canActivate: [validAccountDetails],
    loadComponent: () => import('../account-phone/account-phone').then((m) => m.AccountPhoneComponent),
    data: {
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountInformation, Breadcrumb.accountInformationPhone],
    },
  },
  {
    path: 'membership',
    canActivate: [validAccountDetails],
    loadComponent: () => import('../account-membership/account-membership').then((c) => c.AccountMembershipComponent),
    data: {
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership],
    },
  },
  {
    path: 'membership/gift',
    canActivate: [validAccountDetails],
    pathMatch: 'full',
    loadComponent: () => import('../gift-card/gift-card').then((c) => c.GiftCardComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipGift],
    },
  },
  {
    path: 'membership/cancel',
    canActivate: [validAccountDetails],
    pathMatch: 'full',
    loadComponent: () => import('../account-cancel/account-cancel').then((c) => c.AccountCancelComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipCancel],
    },
  },
  {
    path: 'membership/renew',
    canActivate: [
      isMembershipStatusAllowed([AccountStatus.PENDING]),
      setMembershipRenewAccountDetails,
      validAccountDetails,
    ],
    pathMatch: 'full',
    loadComponent: () => import('../membership-renew/membership-renew').then((c) => c.MembershipRenewComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipRenew],
    },
  },
  {
    path: 'membership/auto-renew',
    canActivate: [validAccountDetails],
    loadComponent: () =>
      import('../membership-auto-renew/membership-auto-renew').then((c) => c.MembershipAutoRenewComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipAutoRenew],
    },
    children: membershipAutoRenewRoutes,
  },
  {
    path: 'membership/associates',
    canActivate: [waitForMembershipLoadGuard],
    pathMatch: 'full',
    loadComponent: () =>
      import('../membership-associates/membership-associates').then((c) => c.MembershipAssociatesComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipAssociates],
    },
  },
  {
    path: 'membership/associate/add',
    canActivate: [
      waitForMembershipLoadGuard,
      canChangeMembership,
      redirectToRenewIfBalanceDue,
      setMembershipAssociateAddAccountDetails,
    ],
    pathMatch: 'full',
    loadComponent: () =>
      import('../membership-associate-add/membership-associate-add').then((c) => c.MembershipAssociateAddComponent),
    data: {
      showImage: true,
      breadcrumb: [
        Breadcrumb.account,
        Breadcrumb.accountMembership,
        Breadcrumb.accountMembershipAssociates,
        Breadcrumb.accountMembershipAssociatesAdd,
      ],
    },
  },
  {
    path: 'membership/plans',
    canActivate: [waitForMembershipLoadGuard, canChangeMembership],
    pathMatch: 'full',
    loadComponent: () => import('../membership-plans/membership-plans').then((c) => c.MembershipPlansComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipPlans],
    },
  },
  {
    path: 'membership/upgrade/plan',
    canActivate: [validAccountDetails, canChangeMembership, redirectToRenewIfBalanceDue],
    pathMatch: 'full',
    loadComponent: () =>
      import('../membership-upgrade-plan/membership-upgrade-plan').then((c) => c.MembershipUpgradePlanComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipPlans],
    },
  },
  {
    path: 'membership/update',
    canActivate: [validAccountDetails, setMembershipUpdateAccountDetails],
    pathMatch: 'full',
    loadComponent: () => import('../membership-update/membership-update').then((c) => c.MembershipUpdateComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipUpdate],
    },
  },
  {
    path: 'membership/card-service',
    canActivate: [validAccountDetails],
    pathMatch: 'full',
    loadComponent: () =>
      import('../membership-card-service/membership-card-service').then((c) => c.MembershipCardServiceComponent),
    data: {
      showImage: true,
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountMembership, Breadcrumb.accountMembershipCardService],
    },
  },
  {
    path: 'insurance',
    loadComponent: () => import('../account-insurance/account-insurance').then((c) => c.AccountInsuranceComponent),
    data: {
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountInsurance],
    },
  },
  {
    path: 'travel',
    loadComponent: () => import('../account-travel/account-travel').then((c) => c.AccountTravelComponent),
    data: {
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountTravel],
    },
  },
  {
    path: 'financial',
    loadComponent: () => import('../account-financial/account-financial').then((c) => c.AccountTravelComponent),
    data: {
      breadcrumb: [Breadcrumb.account, Breadcrumb.accountFinancial],
    },
  },
  // lazy load error module
  {
    path: '**',
    loadComponent: () => import('../not-found/not-found').then((c) => c.NotFound),
  },
];
